import { getLanguageMeta } from '@/utils/general/meta'
import { ISuggestion } from '@/types/home'

/**
 * Get platform type for API calls
 * Returns: "PWA" | "AndroidApp" | "iPhoneApp"
 */
export const getPlatformType = (): string => {
    // Desktop PWA - this can be extended for mobile platforms
    return 'PWA'
}

/**
 * Get selling country from UTM parameters or locale
 * Returns 2-letter country code (e.g., "IN")
 */
export const getSellingCountry = (): string => {
    // TODO: Add utmGetUserPrefCurrency() check if available
    // For now, using locale from getLanguageMeta
    const locale = getLanguageMeta().locale
    return locale || 'IN'
}

/**
 * Extract country code from airport suggestion string
 * Format: "Airport Name, Country - Country Code"
 * Example: "Indira Gandhi International Airport, India - IN"
 */
export const getCountryCodeFromAirportString = (airportString?: string): string | undefined => {
    if (!airportString) {
        return undefined
    }
    try {
        const parts = airportString.split(',')
        if (parts.length > 1) {
            const countryPart = parts[1].trim()
            const countryCodeMatch = countryPart.match(/- ([A-Z]{2})$/)
            if (countryCodeMatch && countryCodeMatch[1]) {
                return countryCodeMatch[1]
            }
        }
        // Fallback: try to extract from format "Country - Code"
        const match = airportString.match(/- ([A-Z]{2})(\s|$)/)
        return match ? match[1] : undefined
    } catch (e) {
        console.error('Error extracting country code from airport string:', e)
        return undefined
    }
}

/**
 * Get country code from location data (for SRP)
 * Searches through locationData object for matching airport code
 */
export const getCountryCodeFromLocationData = (
    locationData: any,
    airportCode?: string
): string | undefined => {
    if (!locationData || !airportCode) {
        return undefined
    }

    try {
        // locationData structure might vary, try common patterns
        const dataObj = locationData.origin || locationData.destination || locationData
        if (dataObj?.countryCode) {
            return dataObj.countryCode
        }

        // Search in array format
        if (Array.isArray(locationData)) {
            const airport = locationData.find(
                (item: any) => item.airportCode === airportCode || item.code === airportCode
            )
            return airport?.countryCode
        }

        // Search in object format
        if (typeof locationData === 'object') {
            const values = Object.values(locationData) as any[]
            const airport = values.find(
                (item: any) =>
                    item?.airportCode === airportCode ||
                    item?.code === airportCode ||
                    item?.k === airportCode
            )
            return airport?.countryCode
        }
    } catch (e) {
        console.error('Error getting country code from location data:', e)
    }

    return undefined
}

/**
 * Get country code from airport suggestions list
 */
export const getCountryCodeFromSuggestions = (
    suggestions: ISuggestion[],
    airportCode: string
): string | undefined => {
    if (!suggestions || !airportCode) {
        return undefined
    }

    try {
        const suggestion = suggestions.find(item => item.k === airportCode)
        if (suggestion?.v) {
            return getCountryCodeFromAirportString(suggestion.v)
        }
    } catch (e) {
        console.error('Error getting country code from suggestions:', e)
    }

    return undefined
}

/**
 * Convert date string (DD/MM/YYYY) to Unix timestamp in milliseconds (as string)
 */
export const convertDateToTimestamp = (dateString: string): string => {
    if (!dateString) {
        return ''
    }

    try {
        // Handle DD/MM/YYYY format
        const [day, month, year] = dateString.split('/').map(Number)
        if (day && month && year) {
            const date = new Date(year, month - 1, day)
            return date.getTime().toString()
        }
    } catch (e) {
        console.error('Error converting date to timestamp:', e)
    }

    // Fallback: try parsing as Date object
    try {
        const date = new Date(dateString)
        if (!isNaN(date.getTime())) {
            return date.getTime().toString()
        }
    } catch (e) {
        console.error('Error parsing date:', e)
    }

    return ''
}

/**
 * Convert Date object to Unix timestamp in milliseconds (as string)
 */
export const convertDateObjectToTimestamp = (date: Date): string => {
    try {
        return date.getTime().toString()
    } catch (e) {
        console.error('Error converting Date object to timestamp:', e)
        return ''
    }
}

/**
 * Get final country code with fallback logic
 * Priority: airport string > locationData > suggestions
 */
export const getFinalCountryCode = (
    airportString?: string,
    locationData?: any,
    airportCode?: string,
    suggestions?: ISuggestion[]
): string | undefined => {
    // Priority 1: Extract from airport string
    let countryCode = getCountryCodeFromAirportString(airportString)
    if (countryCode) {
        return countryCode
    }

    // Priority 2: Get from locationData
    countryCode = getCountryCodeFromLocationData(locationData, airportCode)
    if (countryCode) {
        return countryCode
    }

    // Priority 3: Get from suggestions
    if (suggestions && airportCode) {
        countryCode = getCountryCodeFromSuggestions(suggestions, airportCode)
        if (countryCode) {
            return countryCode
        }
    }

    return undefined
}

